import { createSelector } from '@reduxjs/toolkit';
import {
  AddOnOptionSelection,
  AddOnType,
  MemberRegistrationType,
  Product,
  ProductProperty,
  PropertyValue,
  UserSelection,
} from '@la/types';
import { getCaseInsensitiveStringMatch } from '@la/utilities';
import { calculatePrice } from 'domains/MemberRegistration/AddOnCard/AddOnPriceCalculator';
import { ProductWithMappedSelections } from 'domains/MemberRegistration/MemberRegistration.types';
import { RootState } from '../store';

export const selectAddOnProducts = (state: RootState) =>
  state.registrationAddOns.addOnProducts;

export const selectRegistrationCost = (state: RootState) =>
  state.registrationAddOns.registrationCost;

export const selectRegistrationType = (state: RootState) =>
  state.registrationAddOns.registrationType;

export const selectSelectionsWithErrors = (state: RootState) =>
  state.registrationAddOns.selectionsWithErrors;

export const selectTaxRate = (state: RootState) =>
  state.registrationAddOns.taxRate;

export const selectAddOnOptionSelections = (state: RootState) =>
  state.registrationAddOns.addOnOptionSelections;

// Derived selectors converted from useRegistrationAddOns hook helpers

// Factory: getAddOnProductById(id)
export const makeSelectAddOnProductById = (id: number) =>
  createSelector(selectAddOnProducts, (products) =>
    products.find((addOn) => addOn.id === id)
  );

// Factory: getAddOnsByType(type)
export const makeSelectAddOnsByType = (type: MemberRegistrationType) =>
  createSelector(selectAddOnProducts, (products: Product[]) =>
    products.filter((addOn) =>
      addOn.assignedRoles.some((role) =>
        getCaseInsensitiveStringMatch(role.role, type)
      )
    )
  );

// Factory: getAddOns(category, type)
export const makeSelectAddOnsByCategoryAndType = (
  category: AddOnType,
  type: MemberRegistrationType
) =>
  createSelector(makeSelectAddOnsByType(type), (addOnsByType: Product[]) =>
    addOnsByType.filter((addOn) =>
      addOn.assignedRoles.some(
        (role) =>
          (category === 'optional' &&
            !role.required &&
            getCaseInsensitiveStringMatch(role.role, type)) ||
          (category === 'required' &&
            role.required &&
            getCaseInsensitiveStringMatch(role.role, type))
      )
    )
  );

// Factory: getRequiredAddOnsByType(type)
export const makeSelectRequiredAddOnsByType = (type: MemberRegistrationType) =>
  createSelector(makeSelectAddOnsByType(type), (addOnsByType: Product[]) =>
    addOnsByType.filter((addOn) =>
      addOn.assignedRoles.some(
        (role) =>
          role.required && getCaseInsensitiveStringMatch(role.role, type)
      )
    )
  );

// Factory: getOptionalAddOnsByType(type)
export const makeSelectOptionalAddOnsByType = (type: MemberRegistrationType) =>
  createSelector(makeSelectAddOnsByType(type), (addOnsByType: Product[]) =>
    addOnsByType.filter((addOn) =>
      addOn.assignedRoles.some(
        (role) =>
          !role.required && getCaseInsensitiveStringMatch(role.role, type)
      )
    )
  );

// Factory: getUserSelectionsByAddOnId(productId)
export const makeSelectUserSelectionsByAddOnId = (productId: number) =>
  createSelector(
    selectAddOnOptionSelections,
    (addOnOptionSelections: AddOnOptionSelection[]) => {
      const filtered = addOnOptionSelections.find(
        (addOnOptionSelection) => addOnOptionSelection.addOnId === productId
      );
      return filtered ? filtered.selections : [];
    }
  );

// Factory: getUserSelectionValuesByProduct(productId)
export const makeSelectUserSelectionValuesByProduct = (
  productId: number,
  userSelections: UserSelection[]
) =>
  createSelector(
    makeSelectAddOnProductById(productId),
    (product: Product | undefined) => {
      if (!product) {
        return '';
      }

      const displayNames: string[] = [];

      const selectedOptionsMap = new Map(
        userSelections.map(({ optionId, selectedValue }) => [
          optionId,
          selectedValue,
        ])
      );

      const sortedProperties: ProductProperty[] = [
        ...(product.properties ?? []),
      ].sort((a, b) => a.propertyOrder - b.propertyOrder);

      for (const property of sortedProperties) {
        const selectedValue = selectedOptionsMap.get(property.id.toString());
        if (selectedValue === undefined) {
          continue;
        }

        const displayName = getDisplayNameForProperty(property, selectedValue);
        if (displayName) {
          displayNames.push(displayName);
        }
      }

      return displayNames.join(', ');
    }
  );

function getDisplayNameForProperty(
  property: ProductProperty,
  selectedValue: string
): string | undefined {
  if (property.inputControlType === 'PICK_LIST') {
    return property.values?.find(
      (val: PropertyValue) => val.id.toString() === selectedValue
    )?.name;
  }

  if (
    ['NUMERIC', 'TEXT_AREA', 'TEXT_BOX'].includes(
      property.inputControlType as string
    )
  ) {
    return selectedValue;
  }
}

// Helper for price calculation (pure)
const calculateProductPrice = (
  product: Product,
  selections: UserSelection[]
): number => {
  let calculatedProductPrice = product.price;

  if (
    product.pricingConfigurations &&
    product.pricingConfigurations.length > 0
  ) {
    calculatedProductPrice = calculatePrice(
      product.price,
      selections,
      product.pricingConfigurations
    );
  }

  return calculatedProductPrice;
};

// Factory: getSelectedProduct(productId)
export const makeSelectSelectedProduct = (productId: number) =>
  createSelector(
    selectAddOnProducts,
    selectAddOnOptionSelections,
    selectTaxRate,
    (addOnProducts, addOnOptionSelections, taxRate) => {
      const selection = addOnOptionSelections.find(
        (s) => s.addOnId === productId && s.quantity > 0
      );
      if (!selection) {
        return null;
      }
      return getAddOnProductData(addOnProducts, selection, taxRate);
    }
  );

// getSelectedProducts()
export const selectSelectedProducts = createSelector(
  selectAddOnProducts,
  selectAddOnOptionSelections,
  selectTaxRate,
  (addOnProducts, addOnOptionSelections, taxRate) => {
    return addOnOptionSelections
      .filter((selection) => selection.quantity > 0)
      .map((selection) =>
        getAddOnProductData(addOnProducts, selection, taxRate)
      )
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }
);

// getTotalProductCost()
export const selectTotalProductCost = createSelector(
  selectSelectedProducts,
  (selectedProducts: ProductWithMappedSelections[]) => {
    const total = selectedProducts.reduce(
      (sum: number, product: ProductWithMappedSelections) => {
        return sum + product.itemCost;
      },
      0
    );
    return Math.round(total * 100) / 100;
  }
);

// Factory: getSelectionsWithErrors()
export const makeSelectSelectionsWithErrorsComputed = () =>
  createSelector(
    selectAddOnOptionSelections,
    selectAddOnProducts,
    selectRegistrationType,
    (addOnOptionSelections, addOnProducts, registrationType) => {
      if (!registrationType) {
        return [] as string[];
      }
      const requiredAddOns = addOnProducts.filter((addOn: Product) =>
        addOn.assignedRoles.some(
          (role) =>
            role.required &&
            getCaseInsensitiveStringMatch(role.role, registrationType)
        )
      );

      const newErrors: string[] = [];
      for (const requiredAddOn of requiredAddOns) {
        for (const property of requiredAddOn.properties ?? []) {
          if (property.required) {
            const matchingSelectionsRecord = addOnOptionSelections.find(
              (addOnOptionSelection) =>
                addOnOptionSelection.addOnId === requiredAddOn.id
            );
            const propertyIdString = property.id.toString();
            const matchingSelection = matchingSelectionsRecord?.selections.find(
              (selection: UserSelection) =>
                selection.optionId === propertyIdString
            );
            if (!matchingSelection) {
              newErrors.push(propertyIdString);
            }
          }
        }
      }
      return newErrors;
    }
  );

export function getAddOnProductData(
  addOnProducts: Product[],
  selection: AddOnOptionSelection,
  taxRate: number
) {
  const product = addOnProducts.find((p) => p.id === selection.addOnId);
  if (!product) {
    return null;
  }

  const price = calculateProductPrice(product, selection.selections);
  const subTotal = price * selection.quantity;
  const salesTax =
    product.salesTaxRequired && taxRate
      ? Math.round(subTotal * taxRate) / 100
      : 0;
  const itemCost = subTotal + salesTax;

  return {
    addOnType: selection.addOnType,
    itemCost,
    productId: product.id,
    productInstanceId: selection.productInstanceId,
    productName: product.name,
    quantity: selection.quantity,
    salesTax,
    salesTaxRequired: product.salesTaxRequired,
    selectionTypes: selection.selections,
    subTotal,
    taxRate,
  };
}
